import {
  Calendar,
  CheckCircle2,
  CircleSlash,
  Clock,
  LogIn,
  LogOut,
  Palmtree,
  Sun,
} from "lucide-react";

export const COLORS = {
  navy: "#1e3a5f",
  teal: "#0f766e",
};

export const STATUS_STYLES = {
  present: {
    bg: "bg-emerald-50",
    text: "text-emerald-800",
    border: "border-emerald-200",
    label: "Present",
    Icon: CheckCircle2,
  },
  absent: {
    bg: "bg-red-50",
    text: "text-red-800",
    border: "border-red-200",
    label: "Absent",
    Icon: CircleSlash,
  },
  leave: {
    bg: "bg-amber-50",
    text: "text-amber-800",
    border: "border-amber-200",
    label: "On Leave",
    Icon: Palmtree,
  },
  "half-day": {
    bg: "bg-sky-50",
    text: "text-sky-800",
    border: "border-sky-200",
    label: "Half Day",
    Icon: Clock,
  },
  holiday: {
    bg: "bg-violet-50",
    text: "text-violet-800",
    border: "border-violet-200",
    label: "Holiday",
    Icon: Sun,
  },
  "week-off": {
    bg: "bg-slate-50",
    text: "text-slate-700",
    border: "border-slate-200",
    label: "Week Off",
    Icon: Calendar,
  },
};

export function StatusBadge({ status, className = "" }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.absent;
  const Icon = s.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${s.bg} ${s.text} ${s.border} ${className}`}
    >
      <Icon size={12} />
      {s.label}
    </span>
  );
}

export function StatusIcon({ status, size = 14 }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.absent;
  const Icon = s.Icon;
  return <Icon size={size} className={s.text} aria-hidden />;
}

export function HoursBar({ hours, max = 10 }) {
  const pct = Math.min(100, Math.round((hours / max) * 100));
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden border border-slate-200">
        <div
          className="h-full bg-teal-600 rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-slate-600 tabular-nums w-10 text-right">
        {hours ? `${hours}h` : "—"}
      </span>
    </div>
  );
}

export function weeklyHoursColor(hours) {
  if (hours >= 40) return "text-emerald-700 font-semibold";
  if (hours >= 20) return "text-amber-700 font-semibold";
  return "text-red-700 font-semibold";
}

export function sumWeeklyHours(recordList) {
  return recordList.reduce((sum, r) => sum + (Number(r.hoursWorked) || 0), 0);
}

export function WeekNavigator({
  weekLabel,
  onPrev,
  onNext,
  prevDisabled = false,
  nextDisabled = false,
  leading = null,
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {leading}
      <div className="hr-week-nav">
        <button
          type="button"
          onClick={onPrev}
          disabled={prevDisabled}
          className="hr-week-btn"
          aria-label="Previous week"
        >
          ←
        </button>
        <div className="hr-week-label flex items-center justify-center gap-2">
          <Calendar size={15} className="text-teal-600 shrink-0" />
          <span>{weekLabel}</span>
        </div>
        <button
          type="button"
          onClick={onNext}
          disabled={nextDisabled}
          className="hr-week-btn"
          aria-label="Next week"
        >
          →
        </button>
      </div>
    </div>
  );
}

export function PunchTimes({ checkIn, checkOut, compact }) {
  if (compact) {
    return (
      <div className="text-[10px] leading-tight text-slate-600 space-y-0.5">
        {checkIn ? (
          <div className="flex items-center gap-0.5">
            <LogIn size={10} className="text-emerald-600" />
            {checkIn}
          </div>
        ) : (
          <span className="text-slate-400">—</span>
        )}
        {checkOut ? (
          <div className="flex items-center gap-0.5">
            <LogOut size={10} className="text-red-500" />
            {checkOut}
          </div>
        ) : null}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1 text-sm text-slate-700">
      <span className="inline-flex items-center gap-1">
        <LogIn size={14} className="text-emerald-600" />
        {checkIn || "—"}
      </span>
      <span className="inline-flex items-center gap-1">
        <LogOut size={14} className="text-red-500" />
        {checkOut || "—"}
      </span>
    </div>
  );
}
